import { Action } from '../../../action/action'
import type DetailsState from '..'
import { SetCustomerName } from './setCustomerName.action'
import { SetCustomerPhone } from './setCustomerPhoneNumber.action'
import { SetCustomerEmail } from './setCustomerEmail.action'
import { SetCompanyAdminName } from './setCompanyAdminName.action'
import { SetCompanyName } from '..'
import { CustomerType, GoDreamGiftCard } from '@raskrask-dk/raskrask-utils'

export const NEXT_STATE = 'NEXT_STATE'

export class Next extends Action<typeof NEXT_STATE, undefined> {
    readonly type = NEXT_STATE
    protected state: DetailsState

    constructor(state: DetailsState) {
        super(state)
        this.state = state
    }

    /**
     * Business Logic:
     * If name, email, phone and conditions is set, then we have the minimum requirement for next state.
     * If giftcardprovider has been chosen, the giftcardcode will have to be set.
     * If thirdparty giftcard provider is chosen, then the thirdpartygiftcardtype also has to be set.
     * If GoDream is selected as thirdpartygiftcardprovider, the godream activation code will also have to be set.
     * @returns boolean: true if doable
     */
    public isDoable(): boolean {
        const customerType = this.state.customerType as CustomerType
        let isDoable = true
        // Private customer case:
        if (customerType === 'Private') {
            const isRequiredFieldMissing = this.isRequiredFieldMissing()
            const isGiftCardOK = this.isGiftCardProperlyProvided()
            isDoable = !isRequiredFieldMissing && isGiftCardOK
        }
        // Company Customer Case
        else if (customerType === 'Company') {
            const isRequiredFieldMissing = this.isRequiredFieldMissing()
            isDoable = !isRequiredFieldMissing
        }
        return isDoable
    }

    private isRequiredFieldMissing(): boolean {
        const {
            customerName,
            customerEmail,
            customerPhone,
            acceptConditions,
            companyAdminName,
            companyName,
        } = this.state.bookingflowStateMachine.getData<DetailsState>()
        const customerType = this.state.customerType as CustomerType
        switch (customerType) {
            case 'Private': {
                const nameIsValid = new SetCustomerName(
                    this.state,
                    customerName
                ).isValid()
                const phoneIsValid = new SetCustomerPhone(
                    this.state,
                    customerPhone || 0
                ).isValid()
                const emailIsValid = new SetCustomerEmail(
                    this.state,
                    customerEmail
                ).isValid()
                return (
                    !nameIsValid ||
                    !emailIsValid ||
                    !phoneIsValid ||
                    !acceptConditions
                )
            }
            case 'Company': {
                const cAdminNameIsValid = new SetCompanyAdminName(
                    this.state,
                    companyAdminName
                ).isValid()
                const cAdminEmailIsValid = new SetCustomerEmail(
                    this.state,
                    customerEmail
                ).isValid()
                const cPhoneIsValid = new SetCustomerPhone(
                    this.state,
                    customerPhone || 0
                ).isValid()
                const cNameIsValid = new SetCompanyName(
                    this.state,
                    companyName
                ).isValid()
                return (
                    !cAdminEmailIsValid ||
                    !cAdminNameIsValid ||
                    !cPhoneIsValid ||
                    !acceptConditions ||
                    !cNameIsValid
                )
            }
            default:
                throw new Error('No CustomerType provided???')
        }
    }
    private isGiftCardProperlyProvided(): boolean {
        const { giftCard } =
            this.state.bookingflowStateMachine.getData<DetailsState>()
        if (giftCard) {
            const { giftCardProvider, activationCode, providerId } =
                giftCard as GoDreamGiftCard
            const isThirdPartyGiftCardTypeChosen = !giftCardProvider
                ? true
                : giftCardProvider === 'RaskRask' && !providerId
                ? true
                : giftCardProvider && providerId
                ? true
                : false
            const isGoDreamActivationCodeSet =
                giftCardProvider !== 'GoDream'
                    ? true
                    : giftCardProvider === 'GoDream' && activationCode
                    ? true
                    : false
            return isThirdPartyGiftCardTypeChosen && isGoDreamActivationCodeSet
        }
        return true
    }

    public isValid(): boolean {
        return this.isDoable()
    }
}
